/**
 * Lógica pura de operaciones de inventario (CC-6: NUNCA usar macros lingui aquí).
 *
 * Una operación (stock_picking) agrupa líneas (stock_move_line) y, al
 * confirmarse (borrador→hecho), materializa un stock_move por línea que
 * actualiza el on-hand derivado (stock_quant). El sentido del movimiento depende
 * del tipo de operación:
 *   - receipt  (recepción)   : SUMA en la ubicación destino.
 *   - delivery (entrega)     : RESTA de la ubicación origen.
 *   - internal (transferencia): RESTA del origen y SUMA en el destino.
 *
 * Las cantidades están en unidades menores (enteros, factor 1000), igual que F1.
 */

export type PickingKind = "receipt" | "delivery" | "internal";
export type PickingState = "draft" | "done";

/** Efecto de un move sobre un quant: +delta en una ubicación dada. */
export interface QuantDelta {
  locationId: string;
  delta: number;
}

/** Separador de clave compuesta (productId + locationId). */
const KEY_SEP = "::";

/** Tipos de operación válidos (para validación/selectores). */
export const PICKING_KINDS: readonly PickingKind[] = ["receipt", "delivery", "internal"] as const;

export function isPickingKind(value: string): value is PickingKind {
  return (PICKING_KINDS as readonly string[]).includes(value);
}

/** Normaliza un string (posiblemente nulo) a un PickingKind válido. */
export function coercePickingKind(value: string | null | undefined): PickingKind {
  return value && isPickingKind(value) ? value : "receipt";
}

/**
 * Resuelve origen/destino de una línea según el tipo de operación y las
 * ubicaciones por defecto del documento. Una recepción no necesita origen; una
 * entrega no necesita destino; una transferencia necesita ambos.
 */
export function resolveLineLocations(
  kind: PickingKind,
  pickingSrc: string | null,
  pickingDst: string | null,
): { srcLocationId: string | null; dstLocationId: string | null } {
  if (kind === "receipt") {
    return { srcLocationId: null, dstLocationId: pickingDst };
  }
  if (kind === "delivery") {
    return { srcLocationId: pickingSrc, dstLocationId: null };
  }
  // internal
  return { srcLocationId: pickingSrc, dstLocationId: pickingDst };
}

/**
 * Calcula los deltas de quant que produce confirmar una línea de operación.
 * Devuelve uno o dos deltas (transferencia = salida + entrada). Cantidades <= 0
 * o ubicaciones faltantes producen lista vacía (línea inválida, se ignora).
 */
export function computeLineQuantDeltas(
  kind: PickingKind,
  qty: number,
  srcLocationId: string | null,
  dstLocationId: string | null,
): QuantDelta[] {
  if (qty <= 0) {
    return [];
  }
  const deltas: QuantDelta[] = [];
  if (kind === "receipt") {
    if (dstLocationId) {
      deltas.push({ locationId: dstLocationId, delta: qty });
    }
  } else if (kind === "delivery") {
    if (srcLocationId) {
      deltas.push({ locationId: srcLocationId, delta: -qty });
    }
  } else {
    // internal: mueve entre ubicaciones (resta de origen, suma a destino).
    if (srcLocationId && dstLocationId) {
      deltas.push({ locationId: srcLocationId, delta: -qty });
      deltas.push({ locationId: dstLocationId, delta: qty });
    }
  }
  return deltas;
}

/**
 * Clave compuesta determinista para (producto, ubicación[, lote]). El lote es
 * opcional (F4): sin lote se trata como cadena vacía, de modo que el on-hand sin
 * rastreo nunca se mezcla con el de un lote concreto.
 */
export function quantKey(
  productId: string,
  locationId: string,
  lotId: string | null = null,
): string {
  return `${productId}${KEY_SEP}${locationId}${KEY_SEP}${lotId ?? ""}`;
}

/** Efecto de una línea sobre el on-hand (producto×ubicación×lote). */
export interface QuantEffect {
  productId: string;
  locationId: string;
  delta: number;
  /** Lote afectado (F4). null = sin rastreo. */
  lotId?: string | null;
}

/**
 * Agrega los deltas de varias líneas por (producto, ubicación, lote). Útil para
 * aplicar todos los moves de un picking en una sola pasada por quant.
 */
export function aggregateQuantDeltas(lines: QuantEffect[]): Map<string, Required<QuantEffect>> {
  const map = new Map<string, Required<QuantEffect>>();
  for (const line of lines) {
    const lotId = line.lotId ?? null;
    const key = quantKey(line.productId, line.locationId, lotId);
    const existing = map.get(key);
    if (existing) {
      existing.delta += line.delta;
    } else {
      map.set(key, { ...line, lotId });
    }
  }
  return map;
}
